import { useState } from 'react'
import { ITEM_DISPLAY_NAMES } from '@/economy/types'
import type { WalletInfo } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'
import { useEconomyStore } from '@/state/useEconomyStore'
import { useShipStore } from '@/state/useShipStore'
import { SHIP_TIERS, SHIP_TIER_ORDER } from '@/ships/shipTypes'
import type { ShipTier } from '@/ships/shipTypes'
import { getDockPlacements, getDockPierEndPosition } from '@/interaction/InteractionDetector'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

export function DockPanel({ interaction, wallet }: PanelProps) {
  const [tab, setTab] = useState<'ships' | 'cargo'>('ships')
  const ownedShips = useShipStore((s) => s.ownedShips)
  const buyShip = useShipStore((s) => s.buyShip)
  const boardShip = useShipStore((s) => s.boardShip)
  const closePanel = useEconomyStore((s) => s.closePanel)
  const [error, setError] = useState<string | null>(null)

  // Find which dock index this interaction corresponds to
  const dockIndex = findDockIndex(interaction)

  // Ships docked here
  const dockedHere = ownedShips.filter((s) => s.dockedAt === dockIndex)

  function handleBuy(tier: ShipTier) {
    const info = SHIP_TIERS[tier]
    if (!wallet || wallet.follis < info.price) {
      setError(`Not enough coin. Need ${info.price}\u0192`)
      return
    }

    // Get dock placement for position
    const docks = getDockPlacements()
    if (dockIndex < 0 || dockIndex >= docks.length) {
      setError('Dock position not found')
      return
    }

    const dock = docks[dockIndex]

    // Spawn the ship at the end of the pier (not the dock hut)
    const pierEnd = getDockPierEndPosition(dockIndex)
    const spawnPos = pierEnd ? pierEnd.clone().normalize() : dock.position.clone().normalize()
    const phi = Math.acos(Math.max(-1, Math.min(1, spawnPos.y)))
    const theta = Math.atan2(spawnPos.x, spawnPos.z)

    // Heading: face outward from dock (into water)
    const dockStructure = dock.layout.structures[0]
    const heading = dockStructure ? dockStructure.rotation : 0

    // Deduct cost (client-side for now)
    const store = useEconomyStore.getState()
    if (store.wallet) {
      store.setWallet({
        ...store.wallet,
        follis: store.wallet.follis - info.price,
        total_follis_value: store.wallet.total_follis_value - info.price,
      })
    }

    buyShip(tier, dockIndex, theta, phi, heading)
    setError(null)
  }

  function handleBoard(shipId: string) {
    // Snap ship position to pier end before boarding
    const ship = ownedShips.find((s) => s.id === shipId)
    if (ship && ship.dockedAt >= 0) {
      const pierEnd = getDockPierEndPosition(ship.dockedAt)
      if (pierEnd) {
        const dir = pierEnd.clone().normalize()
        const newPhi = Math.acos(Math.max(-1, Math.min(1, dir.y)))
        const newTheta = Math.atan2(dir.x, dir.z)
        const docks = getDockPlacements()
        const dock = docks[ship.dockedAt]
        const dockStructure = dock?.layout.structures[0]
        const newHeading = dockStructure ? dockStructure.rotation : ship.heading
        useShipStore.setState((s) => ({
          ownedShips: s.ownedShips.map((sh) =>
            sh.id === shipId ? { ...sh, theta: newTheta, phi: newPhi, heading: newHeading } : sh
          ),
        }))
      }
    }
    boardShip(shipId)
    closePanel()
  }

  return (
    <>
      <div className="panel-tabs">
        <button
          className={`panel-tab ${tab === 'ships' ? 'panel-tab--active' : ''}`}
          onClick={() => setTab('ships')}
        >
          Ships
        </button>
        <button
          className={`panel-tab ${tab === 'cargo' ? 'panel-tab--active' : ''}`}
          onClick={() => setTab('cargo')}
        >
          Cargo
        </button>
      </div>

      {error && <div className="panel-error">{error}</div>}

      {tab === 'ships' && (
        <>
          {/* Ships docked here */}
          {dockedHere.length > 0 && (
            <div className="panel-section">
              <h3 className="panel-section__title">Your Ships</h3>
              {dockedHere.map((ship) => {
                const info = SHIP_TIERS[ship.tier]
                const healthPct = Math.round((ship.health / ship.maxHealth) * 100)
                return (
                  <div key={ship.id} className="dock-ship-card">
                    <div className="dock-ship-card__info">
                      <div className="dock-ship-card__name">{info.name}</div>
                      <div className="dock-ship-card__stats">
                        Hull: {healthPct}% &middot; {info.cannonsPerSide} cannons/side
                      </div>
                    </div>
                    <button
                      className="panel-btn panel-btn--primary"
                      onClick={() => handleBoard(ship.id)}
                    >
                      Board
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Ships for sale */}
          <div className="panel-section">
            <h3 className="panel-section__title">Ships for Sale</h3>
            {SHIP_TIER_ORDER.map((tier) => {
              const info = SHIP_TIERS[tier]
              const canAfford = wallet && wallet.follis >= info.price
              return (
                <div key={tier} className="dock-ship-card">
                  <div className="dock-ship-card__info">
                    <div className="dock-ship-card__name">{info.name}</div>
                    <div className="dock-ship-card__desc">{info.description}</div>
                    <div className="dock-ship-card__stats">
                      HP {info.maxHealth} &middot; Speed {info.speed} &middot;
                      {info.cannonsPerSide} cannons/side &middot; {info.mastCount} mast{info.mastCount > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="dock-ship-card__buy">
                    <div className="dock-ship-card__price">{info.price}&fnof;</div>
                    <button
                      className="panel-btn panel-btn--primary"
                      disabled={!canAfford}
                      onClick={() => handleBuy(tier)}
                    >
                      Buy
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {tab === 'cargo' && (
        <div className="panel-section">
          <h3 className="panel-section__title">Ship Cargo</h3>
          {(!wallet || wallet.cargo.length === 0)
            ? <p className="panel-placeholder">No cargo aboard.</p>
            : wallet.cargo.map(([item, qty]) => (
                <div key={item} className="panel-row">
                  <span className="panel-row__label">{ITEM_DISPLAY_NAMES[item]}</span>
                  <span className="panel-row__value">&times;{qty}</span>
                </div>
              ))
          }
        </div>
      )}
    </>
  )
}

/** Find the dock index from the interaction target */
function findDockIndex(interaction: InteractionTarget): number {
  // Dock interactions set villageIndex = -1, and we use structureIndex concept
  // But in InteractionDetector, docks are iterated with `di` index
  // The distance-based matching is the best we can do
  const docks = getDockPlacements()
  if (interaction.villageName.startsWith('Dock #')) {
    const idx = parseInt(interaction.villageName.replace('Dock #', ''), 10)
    if (idx >= 0 && idx < docks.length) return idx
  }
  return 0
}
